import React, { useState, useEffect } from 'react';
import { generateRequestDetailsDraft, sendEmailViaGraphAPI, sendEmailViaGmail, authenticateWithGmail } from '../../utils/sendEmail';
import { toast } from 'react-toastify';
import Loader from './Loader';
import { FaEye, FaEdit, FaGoogle } from 'react-icons/fa';


const EmailDraftModal = ({ customerData, msalInstance, account, onClose, action = 'requestDetails', taxType = 'CVITP', customData = {} }) => {
  const [emailDraft, setEmailDraft] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isEditingBody, setIsEditingBody] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [senderMode, setSenderMode] = useState('graph');
  const [isSigningIn, setIsSigningIn] = useState(false);

  const customDataString = JSON.stringify(customData);

  // 1. Generate the draft as soon as the modal opens
  useEffect(() => {
    const parsedCustomData = JSON.parse(customDataString);
    const prepareDraft = async () => {
      try {
        const draft = await generateRequestDetailsDraft({
          customerEmail: customerData.email,
          customerData: customerData,
          msalInstance,
          account,
          templateType: taxType.toLowerCase(),
          action,
          customData: parsedCustomData
        });
        setEmailDraft(draft);
      } catch (error) {
        setDraftError("Failed to prepare draft: " + error.message);
      }
    };
    
    prepareDraft();
  }, [customerData, msalInstance, account, onClose, action, taxType, customDataString]);

  // 2. Handle the send flow
  const handleSendEmail = async () => {
    setIsSending(true);
    try {
      const emailPayload = { ...emailDraft, attachments };

      if (senderMode === 'gmail') {
        await authenticateWithGmail();
        const result = await sendEmailViaGmail(emailPayload);
        toast.success(result?.fallbackUsed ? 'Gmail compose opened. Please review and send the draft from the popup window.' : 'Email sent successfully via Gmail!');
      } else {
        await sendEmailViaGraphAPI(msalInstance, account, emailPayload, taxType.toLowerCase());
        toast.success('Email sent successfully!');
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Failed to send email. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleGmailLogin = async () => {
    setIsSigningIn(true);
    try {
      await authenticateWithGmail();
      setSenderMode('gmail');
      toast.success('Google sign-in ready. You can send via Gmail now.');
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Google sign-in failed.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Data = event.target.result.split(',')[1];
        setAttachments(prev => [...prev, {
          "@odata.type": "#microsoft.graph.fileAttachment",
          name: file.name,
          contentType: file.type || "application/octet-stream",
          contentBytes: base64Data
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  if (draftError) return (
    <div className="modal-backdrop fade show d-flex justify-content-center align-items-center bg-white" style={{ zIndex: 1600, opacity: 0.9 }}>
      <div className="text-center p-4 bg-light rounded shadow border" style={{ maxWidth: '400px' }}>
        <div className="fs-1 text-danger mb-2">⚠️</div>
        <div className="fw-bold text-danger mb-3">{draftError}</div>
        <button className="btn btn-secondary px-4" onClick={onClose}>Close</button>
      </div>
    </div>
  );

  if (!emailDraft) return <Loader variant="overlay" show={true} text="Preparing email draft..." />;

  return (
    <div className="modal-backdrop fade show" style={{ zIndex: 1600, display: 'block', opacity: 1, backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content position-relative">
          <Loader variant="overlay" show={isSending} text="Sending email..." />
          <div className="modal-header">
            <h5 className="modal-title">Review Email Draft</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isSending}></button>
          </div>
          
          <div className="modal-body">
            <div className="row mb-3 align-items-end">
              <div className="col">
                <label className="fw-bold form-label">To:</label>
                <input type="email" className="form-control" value={emailDraft.to} onChange={(e) => setEmailDraft({ ...emailDraft, to: e.target.value })} disabled={isSending} />
              </div>
              <div className="col-auto d-flex gap-2">
                <button className="btn btn-outline-secondary" onClick={handleGmailLogin} disabled={isSending || isSigningIn}>
                  {isSigningIn ? 'Connecting...' : <><FaGoogle className="me-2" /> Gmail</>}
                </button>
                <button className="btn btn-primary" onClick={handleSendEmail} disabled={isSending || isSigningIn}>
                  {isSending ? 'Sending...' : senderMode === 'gmail' ? 'Send via Gmail' : 'Send Email'}
                </button>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-text text-muted">
                Choose Gmail to sign in on demand and send from the app overlay. Microsoft Graph remains available as the default fallback.
              </div>
            </div>
            <div className="mb-3">
              <label className="fw-bold form-label">Subject:</label>
              <input type="text" className="form-control" value={emailDraft.subject} onChange={(e) => setEmailDraft({ ...emailDraft, subject: e.target.value })} disabled={isSending} />
            </div>
            
            {(action === 'sendInvoice' || action === 'sendConfirmation') && (
              <div className="mb-3 p-3 border rounded bg-white">
                <label className="fw-bold form-label text-primary">📎 Attach Document:</label>
                <div className="form-text mb-2 text-muted">
                  {action === 'sendInvoice' ? 'Since browsers prevent automatic PDF capture during printing, please save the invoice via the print dialog, then attach it here.' : 'Attach any relevant document for the confirmation.'}
                </div>
                <input type="file" className="form-control" onChange={handleFileChange} disabled={isSending} />
                {attachments.length > 0 && (
                  <ul className="list-group mt-2">
                    {attachments.map((att, idx) => (
                      <li key={idx} className="list-group-item d-flex justify-content-between align-items-center py-1 bg-light border-0 mt-1">
                        <span className="small text-truncate fw-medium">{att.name}</span>
                        <button type="button" className="btn btn-sm text-danger py-0 fw-bold fs-5" onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}>&times;</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="fw-bold form-label mb-0">Message Body:</label>
                <button 
                  type="button"
                  className="btn btn-sm btn-outline-secondary" 
                  onClick={() => setIsEditingBody(!isEditingBody)}
                  disabled={isSending}
                >
                  {isEditingBody ? <span className="d-flex align-items-center gap-2"><FaEye /> Preview HTML</span> : <span className="d-flex align-items-center gap-2"><FaEdit /> Edit HTML Source</span>}
                </button>
              </div>
              {isEditingBody ? (
                <textarea 
                  className="form-control" 
                  value={emailDraft.body} 
                  onChange={(e) => setEmailDraft({ ...emailDraft, body: e.target.value })} 
                  disabled={isSending}
                  style={{ minHeight: '300px', fontFamily: 'monospace', fontSize: '13px' }}
                />
              ) : (
                <div 
                  style={{ padding: '15px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#f8f9fa' }}
                  dangerouslySetInnerHTML={{ __html: emailDraft.body }} 
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDraftModal;